/**
 * Configuration Service
 * Fetches and manages app configuration from remote URL
 */

const REMOTE_CONFIG_URL = 'https://dynamicdivas.in/info.json';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

let cachedConfig = null;
let lastFetchTime = 0;

// Role priority hierarchy (higher number = higher privilege)
// Used for menu access control
const ROLE_PRIORITY = {
  'super_admin': 50,
  'admin': 40,
  'special': 30,
  'user': 20,
  'guest': 10,
  
};

/**
 * Fetch configuration from remote URL
 * @returns {Promise<Object|null>} Configuration object or null on error
 */
export const fetchRemoteConfig = async () => {
  // Skip fetch in development (localhost) due to CORS restrictions
  // In production, the app is served from the same domain, so CORS won't be an issue
  if (typeof window !== 'undefined') {
    const isLocalhost = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' || 
                       window.location.hostname === '0.0.0.0' ||
                       window.location.origin.includes('localhost') ||
                       window.location.origin.includes('127.0.0.1');
    
    if (isLocalhost) {
      // In development, return null silently - will use default config
      // This prevents CORS errors from cluttering the console
      return null;
    }
  }

  try {
    const response = await fetch(REMOTE_CONFIG_URL, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Cache-Control': 'no-cache',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch config: ${response.statusText}`);
    }

    const config = await response.json();
    
    // Cache the config
    cachedConfig = config;
    lastFetchTime = Date.now();
    
    return config;
  } catch (error) {
    // Only log error if not a CORS issue (CORS errors are expected in dev)
    if (!error.message?.includes('CORS') && !error.message?.includes('Failed to fetch')) {
      console.error('Error fetching remote configuration:', error);
    }
    return null;
  }
};

/**
 * Get configuration with caching support
 * Returns cached config if still valid, otherwise fetches new one
 * @param {boolean} forceRefresh - Force fetch new config ignoring cache
 * @returns {Promise<Object|null>} Configuration object or null
 */
export const getConfig = async (forceRefresh = false) => {
  const now = Date.now();
  const cacheIsValid = cachedConfig && (now - lastFetchTime) < CACHE_DURATION;

  if (!forceRefresh && cacheIsValid) {
    return cachedConfig;
  }

  const config = await fetchRemoteConfig();
  
  // If fetch returns null (CORS in dev), return default config instead
  if (config === null) {
    return getDefaultConfig();
  }

  return config;
};

/**
 * Get Android configuration
 * @returns {Promise<Object>} Android config object
 */
export const getAndroidConfig = async () => {
  const config = await getConfig();
  return config?.android || {};
};

/**
 * Get specific config value
 * @param {string} key - Config key path (e.g., 'android.latestVersion')
 * @returns {Promise<any>} Config value or undefined
 */
export const getConfigValue = async (key) => {
  const config = await getConfig();
  if (!config) return undefined;

  // Support nested keys like 'android.latestVersion'
  const keys = key.split('.');
  let value = config;
  
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      return undefined;
    }
  }
  
  return value;
};

/**
 * Get disabled menu items for a specific role
 * @param {string} role - User role (e.g., 'Guest', 'user', 'admin')
 * @returns {Promise<Array<string>>} Array of disabled menu item IDs
 */
export const getDisabledMenuItems = async (role = 'guest') => {
  const config = await getConfig();
  
  if (!config?.DisabledMenuItems) {
    return [];
  }

  // Admin and super_admin users should see everything
  const rolePriority = ROLE_PRIORITY[role] || 0;
  if (rolePriority >= ROLE_PRIORITY['admin']) {
    return []; // Admins see all menus
  }

  // Guest users should see everything (no restrictions)
  if (role === 'guest' || role === 'Guest') {
    return [];
  }

  // Support both array format and role-specific format
  if (Array.isArray(config.DisabledMenuItems)) {
    // Simple array format - applies to all non-admin users
    return config.DisabledMenuItems;
  }

  // Role-specific format
  if (typeof config.DisabledMenuItems === 'object') {
    // Try exact match first
    if (config.DisabledMenuItems[role]) {
      return config.DisabledMenuItems[role];
    }
    
    // Try case-insensitive match (for backwards compatibility with "Guest" vs "guest")
    const roleKey = Object.keys(config.DisabledMenuItems).find(
      key => key.toLowerCase() === role.toLowerCase()
    );
    
    return roleKey ? config.DisabledMenuItems[roleKey] : [];
  }

  return [];
};

/**
 * Check if a menu item is disabled for a role
 * @param {string} menuItemId - Menu item identifier
 * @param {string} role - User role
 * @returns {Promise<boolean>} True if menu item should be disabled
 */
export const isMenuItemDisabled = async (menuItemId, role = 'guest') => {
  const disabledItems = await getDisabledMenuItems(role);
  return disabledItems.includes(menuItemId);
};

/**
 * Clear cached configuration
 * Useful for forcing a refresh
 */
export const clearConfigCache = () => {
  cachedConfig = null;
  lastFetchTime = 0;
};

/**
 * Get fallback/default configuration
 * Used when remote config is unavailable
 * @returns {Object} Default configuration
 */
export const getDefaultConfig = () => {
  return {
    android: {
      latestVersion: "1.0.0",
      checkIntervalInMilliseconds: 30000,
      PlayStoreUrl: "https://play.google.com/store/apps/details?id=com.DynamicDivas",
      MinCreditRequired: 100,
      credits_required_For_SingleSpin: 100,
      AddCredits: "{credits_required_For_SingleSpin * 2}",
      DeductCredits: "{credits_required_For_SingleSpin}",
      Adminid: ""
    },
    DisabledMenuItems: []
  };
};

/**
 * Get configuration with fallback
 * Returns remote config if available, otherwise returns default config
 * @returns {Promise<Object>} Configuration object (guaranteed to return something)
 */
export const getConfigWithFallback = async () => {
  const config = await getConfig();
  return config || getDefaultConfig();
};

export default {
  fetchRemoteConfig,
  getConfig,
  getAndroidConfig,
  getConfigValue,
  getDisabledMenuItems,
  isMenuItemDisabled,
  clearConfigCache,
  getDefaultConfig,
  getConfigWithFallback,
};

